<template lang="pug">
  .pagination.center(v-if="totalPages > 1")
    .small
      .mobile-button-container
        Row
          Column(mobile="6")
            RogueButton(
              iconLeft="arrow-left"
              @click="$router.push(getRouterLinkTo(pageNumber - 1))"
              width="full"
              :disabled="pageNumber <= 1"
            )
              | {{ __('Previous') }}
          Column(mobile="6")
            RogueButton(
              @click="$router.push(getRouterLinkTo(pageNumber + 1))"
              arrowRight="arrow-right"
              width="full"
              :disabled="pageNumber >= totalPages"
            )
              | {{ __('Next') }}
          Column(width="12")
            p.status {{ __('Page {pageNumber} of {totalPages}', {pageNumber, totalPages}) }}
    .large
      rogue-link(
        v-if="pageNumber > 1"
        :to="getRouterLinkTo(pageNumber - 1)"
        aria-label="Category Pagination Back"
      )
        Icon.icon-size-xxs( name="arrow-left" fill="black" )
      rogue-link.page-number(
        v-if="pageNumbers[0] > 1"
        :aria-label="'First page'"
        :to="getRouterLinkTo(1)"
      ) 1
      rogue-link.dot-dot-dot(
        v-if="pageNumbers[0] > 1"
        :aria-label="'Jump backwards'"
        :to="jumpPages(false)"
      ) ...
      rogue-link.page-number(
        v-for="i in pageNumbers"
        :key="i"
        :aria-label="'Jump to page '+(i + 1)"
        :delta-from-active="Math.abs(pageNumber - i)"
        :to="getRouterLinkTo(i)"
        :class="{ active: i === pageNumber }"
      ) {{ i }}
      rogue-link.dot-dot-dot(
        v-if="pageNumbers[Math.floor(pageNumbers.length / 2)] < (totalPages - 3)"
        :aria-label="'Jump forwards'"
        :to="jumpPages(true)"
      ) ...
      rogue-link.page-number(
        v-if="pageNumbers[pageNumbers.length - 1] < totalPages"
        :aria-label="'Jump to last page'"
        :to="getRouterLinkTo(totalPages)"
      ) {{ totalPages }}
      rogue-link(
        v-if="pageNumber < totalPages"
        aria-label="Category Pagination Next"
        :to="getRouterLinkTo(pageNumber + 1)"
      )
        Icon.icon-size-xxs(
          name="arrow-right"
          fill="black"
        )
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
// MAX_PAGES_TO_SHOW should be an odd number for design
const MAX_PAGES_TO_SHOW = 5

const props = defineProps({
  pageNumber: {
    type    : Number,
    default : 1,
  },

  totalPages: {
    type    : Number,
    default : 1,
  },
})

const route = useRoute()

/**
 * Return page numbers based on pageNumber and totalPages
 * @return {Number}
 */
const pageNumbers = computed<number[]>(() => {
  const currentPageNumbers = [props.pageNumber]
  let pageBefore = props.pageNumber - 1
  let pageAfter = props.pageNumber + 1

  for (let i = 0; currentPageNumbers.length < MAX_PAGES_TO_SHOW && i < MAX_PAGES_TO_SHOW; i += 1) {
    if (pageAfter <= props.totalPages) {
      currentPageNumbers.push(pageAfter)
      pageAfter += 1
    }

    if (
      pageBefore > 0
      && currentPageNumbers.length < MAX_PAGES_TO_SHOW
    ) {
      currentPageNumbers.unshift(pageBefore)

      if (
        props.pageNumber > props.totalPages - MAX_PAGES_TO_SHOW
        && pageBefore <= props.totalPages - MAX_PAGES_TO_SHOW
      ) {
        currentPageNumbers.shift()
      }

      pageBefore -= 1
    }

    if (pageBefore < 1 && pageAfter >= props.totalPages) {
      break
    }
  }

  return currentPageNumbers
})

function getRouterLinkTo(pageNumber: number) {
  const query = { ...route.query }

  if (pageNumber === 1 || pageNumber === 0) {
    delete query.page_number
  } else {
    query.page_number = pageNumber.toString()
  }

  return { query }
}

/**
 * Jump a set distance from the current page
 * if close to either end of the list jump to
 * one short of the end
 * @param {Boolean} forward jump is forward in direction
 */
function jumpPages(forward = true) {
  const jumpDistance = 3
  let page = forward ? pageNumbers.value[MAX_PAGES_TO_SHOW - 1] + jumpDistance : pageNumbers.value[0] - jumpDistance

  if (page <= 1) {
    page = 2
  } else if (page >= props.totalPages) {
    page = props.totalPages - 1
  }

  return getRouterLinkTo(page)
}
</script>

<style lang="scss" scoped>
  .pagination {
    border-top: $border-width-thin solid $color-gray-light;
    padding-top: $size-s;
    margin-top: $size-s;

    .small {
      @media(min-width: $tablet) {
        display: none;
      }

      .paragraph {
        text-align: center;
      }
    }

    .large {
      display: none;

      @media(min-width: $tablet) {
        display: block;
      }
    }

    .large > * {
      display: inline-flex;
      width: $space-xl;
      height: $space-xl;
      line-height: 2;
      font-size: $size-s;
      align-items: center;
      justify-content: center;

      &:hover {
        background: $color-gray-light;
      }
    }

    :deep(a .icon svg) {
      align-self: center;
    }
  }

  .page-number {
    text-decoration: none;
    color: $color-black;

    &.active {
      font-weight: bold;
      background: $color-gray-light;
    }
  }

  span {
    cursor: pointer;
  }

  a {
    text-decoration: none;
    color: unset;
  }
</style>
